/**
 * Frontend Telemetry Client
 *
 * Batches and sends telemetry events to the analytics-service.
 * Uses sendBeacon for reliability (survives page navigation).
 *
 * Events are queued locally and flushed:
 * - When batch reaches 10 events
 * - Every 5 seconds (whichever comes first)
 * - On page unload (via beforeunload)
 *
 * API: POST /v1/analytics/events/batch
 * Backend: analytics-service/internal/handlers/events.go
 */

import { config } from '@/shared/config'

// --- Types ---

export type EventProperties = Record<string, unknown>

export interface TelemetryEvent {
  eventType: string
  timestamp: string
  sessionId: string
  pagePath: string
  properties: EventProperties
}

/** Convert TelemetryEvent to snake_case for API serialization */
function toApiEvent(event: TelemetryEvent): Record<string, unknown> {
  return {
    event_type: event.eventType,
    timestamp: event.timestamp,
    session_id: event.sessionId,
    page_path: event.pagePath,
    properties: event.properties,
  }
}


// --- Session Management ---

let sessionId: string | null = null

function getSessionId(): string {
  if (!sessionId) {
    sessionId = sessionStorage.getItem('agion_session_id')
    if (!sessionId) {
      sessionId = crypto.randomUUID()
      sessionStorage.setItem('agion_session_id', sessionId)
    }
  }
  return sessionId
}

// --- Event Queue ---

const queue: TelemetryEvent[] = []
let flushTimer: ReturnType<typeof setTimeout> | null = null

const BATCH_SIZE = 10
const FLUSH_INTERVAL_MS = 5000

/**
 * Track a telemetry event.
 *
 * Events are batched and sent to the backend asynchronously.
 * Fire-and-forget: errors are silently ignored.
 *
 * @param eventType - Event type (search_query, search_result_click, search_dismiss, page_view)
 * @param properties - Optional event-specific properties
 */
export function trackEvent(eventType: string, properties: EventProperties = {}): void {
  queue.push({
    eventType,
    timestamp: new Date().toISOString(),
    sessionId: getSessionId(),
    pagePath: window.location.pathname,
    properties,
  })

  if (queue.length >= BATCH_SIZE) {
    flush()
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS)
  }
}

/**
 * Flush queued events to the backend.
 *
 * Uses sendBeacon for reliability (survives page navigation).
 * Falls back to fetch with keepalive for environments without sendBeacon.
 */
function flush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer)
    flushTimer = null
  }

  if (queue.length === 0) {
    return
  }

  const batch = queue.splice(0)
  // Convert to snake_case for API serialization
  const payload = { events: batch.map(toApiEvent) }

  // API base from config (matches pattern used by UnifiedAPIClient)
  const apiBase = config.services.api
  const url = `${apiBase}/v1/analytics/events/batch`
  const body = JSON.stringify(payload)

  // Prefer sendBeacon (reliable during page unload)
  if (navigator.sendBeacon) {
    const blob = new Blob([body], { type: 'application/json' })
    navigator.sendBeacon(url, blob)
  } else {
    // Fallback for environments without sendBeacon
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {
      // Fire-and-forget: telemetry should never block or error
    })
  }
}

// --- Search Telemetry Helpers ---

/**
 * Track a search query event.
 *
 * @param query - Search query string
 * @param resultCount - Number of results returned
 * @param intent - Optional detected search intent (navigational, semantic, etc.)
 * @param latencyMs - Optional search latency in milliseconds
 */
export function trackSearchQuery(
  query: string,
  resultCount: number,
  intent?: string,
  latencyMs?: number
): void {
  trackEvent('search_query', {
    query,
    result_count: resultCount,
    intent,
    latency_ms: latencyMs,
  })
}

/**
 * Track a search result click event.
 *
 * @param query - Original search query
 * @param resultId - ID of the clicked result
 * @param resultRank - Position of the result in the list (1-indexed)
 * @param resultType - Type of the result (mission, agent, rule, etc.)
 */
export function trackSearchClick(
  query: string,
  resultId: string,
  resultRank: number,
  resultType: string
): void {
  trackEvent('search_result_click', {
    query,
    result_id: resultId,
    result_rank: resultRank,
    result_type: resultType,
  })
}

/**
 * Track a search dismiss event (user closes search without clicking a result).
 *
 * @param query - Search query that was dismissed
 * @param resultCount - Number of results that were shown
 * @param timeOnResultsMs - Time spent viewing results before dismissing
 */
export function trackSearchDismiss(
  query: string,
  resultCount: number,
  timeOnResultsMs: number
): void {
  trackEvent('search_dismiss', {
    query,
    result_count: resultCount,
    time_on_results_ms: timeOnResultsMs,
  })
}

// --- Page Unload Handler ---

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush)
}
